import Tile from 'ol/Tile';
import { ImageTile } from 'ol';
import TileState from 'ol/TileState';
import { LayerOptions } from '../tranformations/wmsToTileLayer';

// Simple in-memory tile cache
const tileCache = new Map<string, { blob: Blob; timestamp: number }>();

function setupLoadedTile(tile: Tile, blob: Blob, layerOptions?: LayerOptions) {
  ((tile as ImageTile).getImage() as HTMLImageElement).src =
    URL.createObjectURL(blob);
  tile.setState(TileState.LOADED);
  if (layerOptions) {
    layerOptions.layerItem.successCount++;
  }
}

export function fetchTileWithRefreshControl(
  refreshRate = 0,
  headers?: Record<string, string>,
  maxRetries = 5,
  layerOptions?: LayerOptions | undefined
) {
  return function (tile: Tile, src: string) {
    const now = Date.now();

    // Check if we have this tile in cache and if it's still valid based on refresh rate
    const cachedTile = tileCache.get(src);
    if (cachedTile && refreshRate > 0) {
      const age = now - cachedTile.timestamp;
      if (age < refreshRate * 1000) {
        setupLoadedTile(tile, cachedTile.blob, layerOptions);
        return;
      }
    }

    // Fall back to the original fetchTileWithRetry if not in cache or expired
    fetchTileWithRetry(
      headers,
      maxRetries,
      layerOptions,
      refreshRate
    )(tile, src);
  };
}

// Keep the original fetchTileWithRetry function unchanged
export function fetchTileWithRetry(
  headers?: Record<string, string>,
  maxRetries = 5,
  layerOptions?: LayerOptions | undefined,
  refreshRate = 0
) {
  return function (tile: Tile, src: string) {
    let attempt = 0;
    const now = Date.now();

    function loadTile() {
      const xhr = new XMLHttpRequest();
      xhr.responseType = 'blob';
      xhr.onload = function () {
        if (xhr.status === 200) {
          const blob = xhr.response;

          if (refreshRate && refreshRate > 0) {
            tileCache.set(src, { blob, timestamp: now });
          }

          setupLoadedTile(tile, blob, layerOptions);
        } else {
          retryLoad(
            new Error(`Failed to load tile: ${xhr.status} ${xhr.statusText}`)
          );
        }
      };
      xhr.onerror = function () {
        retryLoad(new Error('Network error occurred during tile load'));
      };
      xhr.open('GET', src);

      // Set headers if they are provided
      if (headers) {
        Object.keys(headers).forEach((key) => {
          xhr.setRequestHeader(key, headers[key]);
        });
      }

      xhr.send();
    }

    function retryLoad(error: Error) {
      if (attempt < maxRetries) {
        attempt++;
        setTimeout(loadTile, 250 * attempt); // Exponential backoff
      } else {
        tile.setState(TileState.ERROR);

        if (layerOptions) {
          layerOptions.layerItem.errorCount++;
          layerOptions.removeLayerOnThresholdExceeded(
            layerOptions.layerItem,
            layerOptions?.onLoadFail
          );
        }
      }
    }

    loadTile();
  };
}
